import { useEffect, useState } from "react";
import { Dialog } from "../Dialog";
import { Padder, SimpleButton, SimpleContainer, SimpleInputWithLabel, SimplePageTemplate } from "./SimpleElements"
import { useUser } from "../../../biz/hooks/entities/useUser";
import { useOrganization } from "../../../biz/hooks/entities/useOrganization";
import { Config } from "../../../biz/data/Config";
import { BillingMonth, BillingMonthPlants, useBilling } from "../../../biz/pricing/useBilling";
import { useDateFunctions, useFormatting } from "../../../biz/translations/useFormatting";
import { useTranslation } from "react-i18next";
import { PortalContainer } from "../main/Portal";
import { OrderFormType } from "./ReadMoreDialog";
import { useFormValidation } from "../../../biz/hooks/ui/useFormValidation";

export const SimpleBilling = () => {
    const { t } = useTranslation();
    const ui = useSimpleBilling();
    const user = useUser();
    const { initializeForm, register, formIsValid } = useFormValidation("start_pro");

    const formDisabled = false;

    return (
        <div>

            <SimplePageTemplate className="billing">
                <div className="billing-header">
                    <h1>
                        {t("menu_billing")}
                    </h1>
                    <div>
                        <SimpleButton
                            onClick={ui.onDialogPress}
                            label={t("billingInformation")}
                            shape="none"
                            type="secondarybeige20"
                            icon="pen"
                        />
                    </div>
                </div>

                <div>
                    <Padder heightPx={32} />
                    <hr />
                    <Padder heightPx={32} />

                </div>

                {user.isDeveloper && (<BillingInfo />)}
            </SimplePageTemplate>


            <PortalContainer visible={ui.showDialog} clickOutside={ui.onDismiss}>
                <div className="billing-dialog">
                    <div className="header">
                        {t("billingInformation")}
                    </div>
                    <Padder heightPx={48} />
                    <div className="inputs frm">


                        <SimpleInputWithLabel
                            disabled={formDisabled}
                            label={t("readmore_form_organization") + " *"}
                            value={ui.model?.customerContactName ?? ""}
                            placeholder={t("readmore_form_organization")}
                            {...register(
                                "customerContactName",
                                "len4",
                                (e: string) => ui.setModel({ ...ui.model, customerContactName: e }),
                                true
                            )}
                        />

                        <SimpleInputWithLabel
                            disabled={formDisabled}
                            label={t("readmore_form_contact") + " *"}
                            value={ui.model?.contact}
                            placeholder={t("readmore_form_contactPlaceholder")}
                            {...register(
                                "contact",
                                "len4",
                                (e: string) => ui.setModel({ ...ui.model, contact: e }),
                                true
                            )}
                        />

                        <SimpleInputWithLabel
                            disabled={formDisabled}
                            label={t("readmore_form_address") + " *"}
                            value={ui.model?.address}
                            placeholder={t("readmore_form_addressPlaceholder")}
                            {...register(
                                "address",
                                "len4",
                                (e: string) => ui.setModel({ ...ui.model, address: e }),
                                true
                            )}
                        />

                        <SimpleInputWithLabel
                            disabled={formDisabled}
                            label={t("readmore_form_postalAddress") + " *"}
                            value={ui.model?.postalAddress}
                            placeholder={t("readmore_form_postalAddressPlaceholder")}
                            {...register(
                                "postalAddress",
                                "len4",
                                (e: string) => ui.setModel({ ...ui.model, postalAddress: e }),
                                true
                            )}
                        />

                        <SimpleInputWithLabel
                            disabled={formDisabled}
                            label={t("readmore_form_organizationnr") + " *"}
                            value={ui.model?.organizationNumber}
                            placeholder={t("readmore_form_organizationnr")}
                            {...register(
                                "organizationNumber",
                                "len8",
                                (e: string) => ui.setModel({ ...ui.model, organizationNumber: e }),
                                true,
                                "Organisationsnumret måste vara minst 8 tecken"
                            )}
                        />

                        <SimpleInputWithLabel
                            disabled={formDisabled}
                            label={t("readmore_form_billingemail") + " *"}
                            value={ui.model?.billingEmail}
                            placeholder={t("readmore_form_billingemailPlaceholder")}
                            {...register(
                                "billingEmail",
                                "email",
                                (e: string) => ui.setModel({ ...ui.model, billingEmail: e }),
                                true
                            )}
                        />

                    </div>

                    <Padder heightPx={32} />
                    <div className="buttons">

                        <div className="flex0">
                            <SimpleButton
                                onClick={ui.onSave}
                                label={t("update")}
                                shape="bigpill"
                                type="primarygreen"
                                icon="check"
                            />
                        </div>
                        <div className="flex0">
                            <SimpleButton
                                onClick={ui.onDismiss}
                                label={t("cancel")}
                                shape="bigpill"
                                type="tertiarybeige50"
                                icon="times"
                            />
                        </div>
                        <div className="flex1"></div>
                    </div>
                </div>

            </PortalContainer>

            <Padder heightPx={48} />
        </div>
    )
}

const BillingInfo = () => {
    const { t } = useTranslation();
    const billing = useBilling();
    const org = useOrganization();
    const df = useFormatting();


    useEffect(() => {
        if (!!org.currentOrganization?.id) {
            billing.load();
        }
    }, [org.currentOrganization])

    return (
        <div>
            <div>
                <div className="billing-item-container">
                    {billing.allBilling?.data?.map(m => (
                        <BillingItem
                            key={m.monthkey} data={m}
                            plants={billing.allBilling?.plants?.find(n => n.monthkey === m?.monthkey)}
                        />
                    ))}
                </div>
                <table hidden className="tbl">
                    <thead>
                        <tr>
                            <th>{t("month")}</th>
                            <th>{t("count")}</th>
                            <th>{t("latest")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {billing.dataPlants?.map(item => (
                            <tr key={item?.monthkey}>
                                <td onClick={async () => { await billing.getBillingPlantsDetails(item.monthkey ?? 0) }}>
                                    {item.monthkey}
                                </td>
                                <td>
                                    {item.count}
                                </td>
                                <td>
                                    {df.epochToDate(item.sys_datemodified_utc_epoch)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* {JSON.stringify(billing.allBilling)} */}
            </div>
        </div>
    )
}

const BillingItem = ({ data, plants }: { data: BillingMonth, plants?: BillingMonthPlants }) => {

    const offersCount = Object.keys(data?.offer_days ?? {}).length;
    const f = useFormatting();

    const { t } = useTranslation();

    return (
        <div className="billing-item">

            <div className="raw">
                const data={JSON.stringify(data, null, 3)}
            </div>
            <div className="raw">
                const plants={JSON.stringify(plants, null, 3)}
            </div>

            <div className="month">
                {t("month")}: {f.monthkeyToDate(data?.monthkey)}
            </div>

            <LabelValue label={t("premium_days")} value={data?.premium_days?.length} />
            <LabelValue label={t("premium_days")} value={f.datekeyToDate(Math.min(...(data?.premium_days ?? [])))} />
            <LabelValue label={t("premium_days")} value={f.datekeyToDate(Math.max(...(data?.premium_days ?? [])))} />

            <LabelValue label={t("offers")} value={offersCount} />
            <LabelValue label={t("plants")} value={plants?.count ?? 0} />

        </div>
    )
}

const LabelValue = ({ label, value }: { label: string, value: string | number }) => {
    return (
        <div>
            {label}: {value}
        </div>
    )
}


const useSimpleBilling = () => {
    const ops = useOrganization();
    const org = useOrganization().currentOrganization;


    const getDefaultModel = () => {
        return {
            customerContactName: (!!org?.customerContactName ? org?.customerContactName : org?.name) ?? "",
            contact: org?.contact ?? "",
            address: org?.address ?? "",
            postalAddress: org?.postalAddress ?? "",
            organizationNumber: org?.organizationNumber ?? "",
            billingEmail: org?.billingEmail ?? "",
        }
    }


    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [model, setModel] = useState<OrderFormType>(getDefaultModel());


    return {
        model: model, setModel: setModel,
        showDialog: showDialog,
        onDialogPress: () => {
            setModel(getDefaultModel());
            setShowDialog(true);
        },
        onDismiss: () => setShowDialog(false),
        onSave: async () => {
            setShowDialog(false);
            ops.updateOrganization
            ops.updateBillingInfo(org?.id ?? "", model);
        },
    }

}